import React, { useState, useMemo } from "react";
import {
  MousePointer2, MessageSquare, Fingerprint, Heart, AlertTriangle,
  Lightbulb, Pencil, Trash2, Plus, Smile, Frown, Meh, Radio,
} from "lucide-react";
import JourneyDetailPanel from "./JourneyDetailPanel";

const SWIMLANES = [
  { key: "actions",   label: "Actions",       icon: MousePointer2, iconCls: "actions",  color: "#2563eb" },
  { key: "thoughts",  label: "Thoughts",      icon: MessageSquare, iconCls: "thoughts", color: "#6366f1" },
  { key: "touch",     label: "Touchpoints",   icon: Fingerprint,   iconCls: "touch",    color: "#10b981" },
  { key: "emotions",  label: "Emotions",      icon: Heart,         iconCls: "emotions", color: "#ec4899" },
  { key: "pains",     label: "Pain Points",   icon: AlertTriangle, iconCls: "pains",    color: "#ef4444" },
  { key: "opps",      label: "Opportunities", icon: Lightbulb,     iconCls: "opps",     color: "#b45309" },
];

const DASH = <span style={{ color: "var(--plm-border)", fontStyle: "italic" }}>—</span>;

const sentimentClass = (score) => {
  if (score == null) return "neutral";
  if (score > 0.3) return "positive";
  if (score < -0.3) return "negative";
  return "neutral";
};

const emotionIcon = (emotion) => {
  if (!emotion) return null;
  const e = emotion.toLowerCase();
  if (/happy|delight|excit|satisf/.test(e)) return <Smile size={14} style={{ color: "var(--plm-success)" }} />;
  if (/frustrat|angry|annoy|sad|confus/.test(e)) return <Frown size={14} style={{ color: "var(--plm-danger)" }} />;
  return <Meh size={14} style={{ color: "var(--plm-warning)" }} />;
};

// Helper: renders items from steps, or a dash if none have content
const renderItems = (stepsInStage, extractor) => {
  const items = stepsInStage.map(s => ({ id: s.id, content: extractor(s) })).filter(i => i.content);
  if (items.length === 0) return DASH;
  return <div>{items.map(i => <React.Fragment key={i.id}>{i.content}</React.Fragment>)}</div>;
};

const JourneyGrid = ({ steps, onEditStep, onDeleteStep, onCreateStep, journeyMapId }) => {
  const [visibleLanes, setVisibleLanes] = useState(() => new Set(SWIMLANES.map(l => l.key)));
  const [selectedStep, setSelectedStep] = useState(null);

  const { stages, stageSteps } = useMemo(() => {
    const sorted = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
    const seen = new Set();
    const stageNames = [];
    for (const s of sorted) {
      const phase = s.phaseName || "Unnamed";
      if (!seen.has(phase)) { seen.add(phase); stageNames.push(phase); }
    }
    const grouped = {};
    for (const name of stageNames) {
      grouped[name] = sorted.filter(s => (s.phaseName || "Unnamed") === name);
    }
    return { stages: stageNames, stageSteps: grouped };
  }, [steps]);

  const toggleLane = (key) => {
    setVisibleLanes(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else { next.add(key); }
      return next;
    });
  };

  const primaryStep = (stageName) => stageSteps[stageName]?.[0] || null;

  const renderCell = (lane, stageName) => {
    const ss = stageSteps[stageName] || [];
    if (ss.length === 0) return DASH;

    switch (lane) {
      case "actions":
        return renderItems(ss, s => s.userAction ? (
          <div style={{ marginBottom: 4 }}>{s.userAction}</div>
        ) : null);

      case "thoughts":
        return renderItems(ss, s => s.thoughtsFeelings ? (
          <div style={{ marginBottom: 4, fontStyle: "italic", color: "var(--plm-text-muted)" }}>
            "{s.thoughtsFeelings}"
          </div>
        ) : null);

      case "touch":
        return renderItems(ss, s => s.touchpoints ? (
          <div className="d-flex align-items-center gap-1" style={{ marginBottom: 2 }}>
            {s.channel && <span className="plm-badge gray" style={{ fontSize: "0.65rem" }}>{s.channel}</span>}
            <span>{s.touchpoints}</span>
          </div>
        ) : null);

      case "emotions":
        return renderItems(ss, s => {
          if (!s.emotion) return null;
          const score = s.sentimentScore;
          const pct = score != null ? Math.round(((score + 1) / 2) * 100) : 50;
          const barColor = score > 0.3 ? "var(--plm-success)" : score < -0.3 ? "var(--plm-danger)" : "var(--plm-warning)";
          return (
            <div className="plm-emotion-bar" style={{ marginBottom: 4 }}>
              <div className={`plm-sentiment-dot ${sentimentClass(score)}`} />
              <div className="plm-sentiment-bar-track">
                <div className="plm-sentiment-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
              </div>
              <span style={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                {emotionIcon(s.emotion)} {s.emotion}
              </span>
            </div>
          );
        });

      case "pains":
        return renderItems(ss, s => s.painPoint ? (
          <div className="plm-cell-chip pain" style={{ marginBottom: 4 }}>
            <AlertTriangle size={11} /> {s.painPoint}
          </div>
        ) : null);

      case "opps":
        return renderItems(ss, s => s.opportunity ? (
          <div className="plm-cell-chip opportunity" style={{ marginBottom: 4 }}>
            <Lightbulb size={11} /> {s.opportunity}
          </div>
        ) : null);

      default:
        return DASH;
    }
  };

  const activeLanes = SWIMLANES.filter(l => visibleLanes.has(l.key));

  return (
    <div>
      {/* Layer toggles */}
      <div className="plm-layer-toggles">
        <span style={{ fontSize: "0.75rem", color: "var(--plm-text-muted)", fontWeight: 600, alignSelf: "center", marginRight: 4 }}>
          Layers:
        </span>
        {SWIMLANES.map(({ key, label, color }) => (
          <button
            key={key}
            className={`plm-layer-toggle ${visibleLanes.has(key) ? "active" : ""}`}
            onClick={() => toggleLane(key)}
            style={visibleLanes.has(key) ? { background: color, borderColor: color } : {}}
          >
            <span className="dot" style={{ background: color }} />
            {label}
          </button>
        ))}
      </div>

      {stages.length === 0 ? (
        <div style={{ textAlign: "center", padding: "32px 0", color: "var(--plm-text-muted)", fontSize: "0.85rem" }}>
          No phases defined yet. Add your first journey phase.
        </div>
      ) : (
        <div className="plm-grid-wrapper" style={{ borderRadius: "var(--plm-radius)", border: "1px solid var(--plm-border)", overflow: "hidden" }}>
          <div
            className="plm-grid"
            style={{ gridTemplateColumns: `130px repeat(${stages.length}, minmax(180px, 1fr))` }}
          >
            {/* Header row */}
            <div className="plm-swimlane-label" style={{ borderBottom: "2px solid var(--plm-primary)", background: "#f8fafc" }}>
              <span style={{ fontSize: "0.72rem" }}>Stage</span>
            </div>
            {stages.map((stageName, idx) => {
              const step = primaryStep(stageName);
              return (
                <div key={stageName} className="plm-stage-col">
                  <div className="plm-stage-number">{idx + 1}</div>
                  <div className="plm-stage-name">{stageName}</div>
                  {step?.channel && (
                    <div className="plm-stage-channel">
                      <Radio size={10} style={{ marginRight: 2 }} /> {step.channel}
                    </div>
                  )}
                  <div className="plm-step-actions">
                    <button className="plm-btn-ghost" style={{ padding: "2px 4px", fontSize: "0.7rem" }}
                      onClick={() => step && onEditStep(journeyMapId, step)}>
                      <Pencil size={11} />
                    </button>
                    <button className="plm-btn-ghost danger" style={{ padding: "2px 4px", fontSize: "0.7rem" }}
                      onClick={() => step && onDeleteStep(journeyMapId, step.id)}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Swimlane rows */}
            {activeLanes.map(({ key, label, icon: Icon, iconCls }) => (
              <React.Fragment key={key}>
                <div className="plm-swimlane-label">
                  <span className={`lane-icon ${iconCls}`}><Icon size={13} /></span>
                  <span>{label}</span>
                </div>
                {stages.map((stageName) => (
                  <div
                    key={`${key}-${stageName}`}
                    className="plm-swimlane-cell clickable"
                    onClick={() => { const s = primaryStep(stageName); if (s) setSelectedStep(s); }}
                  >
                    {renderCell(key, stageName)}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Add Step */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <button className="plm-btn-ghost" onClick={() => onCreateStep(journeyMapId)}>
          <Plus size={14} /> Add Phase
        </button>
      </div>

      {/* Detail Panel */}
      {selectedStep && (
        <JourneyDetailPanel
          step={selectedStep}
          onClose={() => setSelectedStep(null)}
          onEdit={(s) => { setSelectedStep(null); onEditStep(journeyMapId, s); }}
          onDelete={(s) => { setSelectedStep(null); onDeleteStep(journeyMapId, s.id); }}
        />
      )}
    </div>
  );
};

export default JourneyGrid;
